import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Suspense } from 'preact/compat'
import { useState } from 'preact/hooks'
import { lazy } from 'preact-iso'
import type { Position } from '../api/generated/Position'
import type { Preferences } from '../api/generated/Preferences'
import { formatDay } from '../shifts/time'
import {
  type AvailabilityForm,
  availabilityForm,
  availabilityRanges,
  type DayAvailability,
  type Hours,
} from './availability'

// Split out with the Markdown renderer, which would grow the main bundle by a third
const JobDescription = lazy(() =>
  import('../positions/JobDescription').then((m) => m.JobDescription),
)

export type PreferencesForm = { days: AvailabilityForm; positionIds: string[] }

export const preferencesForm = (preferences: Preferences, days: string[]): PreferencesForm => ({
  days: availabilityForm(preferences.availability, days),
  positionIds: preferences.position_ids,
})

export const preferencesOf = (form: PreferencesForm): Preferences => ({
  availability: availabilityRanges(form.days),
  position_ids: form.positionIds,
})

const inputValue = (e: Event) => (e.target as HTMLInputElement).value
const shrink = { inputLabel: { shrink: true } }
const defaultHours: Hours = { start: '08:00', end: '12:00' }

function DayField({
  day,
  value,
  disabled,
  onChange,
}: {
  day: string
  value: DayAvailability
  disabled?: boolean
  onChange: (value: DayAvailability) => void
}) {
  const hours = value.kind === 'hours' ? value.hours : []
  const setHours = (next: Hours[]) =>
    onChange(next.length > 0 ? { kind: 'hours', hours: next } : { kind: 'none' })
  const editHours = (index: number, change: Partial<Hours>) =>
    setHours(hours.map((h, i) => (i === index ? { ...h, ...change } : h)))
  return (
    <Stack spacing={1}>
      <TextField
        select
        label={formatDay(day)}
        disabled={disabled}
        value={value.kind}
        onChange={(e) => {
          const kind = inputValue(e)
          onChange(
            kind === 'hours' ? { kind, hours: [defaultHours] } : { kind: kind as 'all' | 'none' },
          )
        }}
        slotProps={{ select: { native: true }, ...shrink }}
      >
        <option value="none">Indisponible</option>
        <option value="all">Toute la journée</option>
        <option value="hours">Certaines heures</option>
      </TextField>
      {hours.map((h, index) => (
        <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            label="De"
            type="time"
            required
            disabled={disabled}
            sx={{ flex: 1 }}
            slotProps={shrink}
            value={h.start}
            onInput={(e) => editHours(index, { start: inputValue(e) })}
          />
          <TextField
            label="À"
            type="time"
            required
            disabled={disabled}
            sx={{ flex: 1 }}
            slotProps={shrink}
            helperText={h.end && h.end <= h.start ? 'Le lendemain' : undefined}
            value={h.end}
            onInput={(e) => editHours(index, { end: inputValue(e) })}
          />
          <IconButton
            aria-label="Retirer cette plage"
            disabled={disabled}
            onClick={() => setHours(hours.filter((_, i) => i !== index))}
          >
            <Delete />
          </IconButton>
        </Stack>
      ))}
      {value.kind === 'hours' && (
        <Button
          size="small"
          startIcon={<Add />}
          disabled={disabled}
          onClick={() => setHours([...hours, defaultHours])}
          sx={{ alignSelf: 'flex-start' }}
        >
          Ajouter une plage
        </Button>
      )}
    </Stack>
  )
}

function PositionField({
  slug,
  position,
  checked,
  disabled,
  onToggle,
}: {
  slug: string
  position: Position
  checked: boolean
  disabled?: boolean
  onToggle: () => void
}) {
  const [showDescription, setShowDescription] = useState(false)
  return (
    <div>
      <FormControlLabel
        control={<Checkbox checked={checked} disabled={disabled} onChange={onToggle} />}
        label={
          <span>
            {position.name}
            {position.location && (
              <Typography component="span" variant="body2" color="text.secondary">
                {` · ${position.location}`}
              </Typography>
            )}
          </span>
        }
      />
      {position.job_description.trim() && (
        <>
          <Button size="small" onClick={() => setShowDescription(!showDescription)}>
            {showDescription ? 'Masquer la fiche de poste' : 'Voir la fiche de poste'}
          </Button>
          <Collapse in={showDescription} unmountOnExit>
            <Suspense fallback={<CircularProgress size={24} />}>
              <JobDescription
                markdown={position.job_description}
                slug={slug}
                positionId={position.id}
              />
            </Suspense>
          </Collapse>
        </>
      )}
    </div>
  )
}

/** A volunteer's availability by day of the event and the positions they would like to hold. */
export function PreferencesFields({
  slug,
  days,
  positions,
  value,
  disabled,
  onChange,
}: {
  slug: string
  days: string[]
  positions: Position[]
  value: PreferencesForm
  disabled?: boolean
  onChange: (value: PreferencesForm) => void
}) {
  const togglePosition = (id: string) =>
    onChange({
      ...value,
      positionIds: value.positionIds.includes(id)
        ? value.positionIds.filter((p) => p !== id)
        : [...value.positionIds, id],
    })
  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          Mes disponibilités
        </Typography>
        {days.length === 0 && (
          <Typography color="text.secondary">
            Les dates de l'événement ne sont pas encore fixées.
          </Typography>
        )}
        {days.map((day) => (
          <DayField
            key={day}
            day={day}
            value={value.days[day] ?? { kind: 'none' }}
            disabled={disabled}
            onChange={(next) => onChange({ ...value, days: { ...value.days, [day]: next } })}
          />
        ))}
      </Stack>
      {positions.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            Postes qui m'intéressent
          </Typography>
          {positions.map((position) => (
            <PositionField
              key={position.id}
              slug={slug}
              position={position}
              checked={value.positionIds.includes(position.id)}
              disabled={disabled}
              onToggle={() => togglePosition(position.id)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
